import { Helmet } from 'react-helmet-async';

const SITE_URL = "https://paisleyscotland.com";
const SITE_NAME = "Paisley Scotland";
const SITE_TWITTER = "@paisleyscotland";
const SITE_LOGO = "https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/1hc89shf_Sophisticated%20Paisley%20Business%20Logo%20with%20Invisible%20Border.PNG";
const SITE_OG_IMAGE = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";

const SEO = ({
  title = "Paisley Business | Local News, Jobs & Business Guide",
  description = "The heart of Paisley's business community. Local news, jobs, business directory, events and sponsorship for ambitious businesses across Renfrewshire, Scotland.",
  keywords = "Paisley Business, Paisley Scotland, paisleyscotland.com, Paisley local news, Paisley jobs, Paisley business guide, Renfrewshire directory, local business Paisley",
  image = SITE_OG_IMAGE,
  url = SITE_URL,
  type = "website",
  structuredData = null,
}) => {
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={SITE_NAME} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_TWITTER} />
      <meta name="twitter:creator" content={SITE_TWITTER} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional Meta */}
      <meta name="theme-color" content="#0EA5E9" />
      <meta name="msapplication-TileColor" content="#0F172A" />

      {/* RSS feed auto-discovery */}
      <link rel="alternate" type="application/rss+xml" title="Paisley Business — News & Insights" href={`${SITE_URL}/api/feed/news.xml`} />
      <link rel="alternate" type="application/rss+xml" title="Paisley Business — Latest Jobs" href={`${SITE_URL}/api/feed/jobs.xml`} />

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="GB-RFW" />
      <meta name="geo.placename" content="Paisley" />
      <meta name="geo.position" content="55.8456;-4.4239" />
      <meta name="ICBM" content="55.8456, -4.4239" />

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": SITE_NAME,
          "alternateName": "Paisley Scotland Sponsorship Platform",
          "description": description,
          "url": SITE_URL,
          "logo": SITE_LOGO,
          "telephone": "+44-7957-166339",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Paisley",
            "addressRegion": "Renfrewshire",
            "postalCode": "PA1",
            "addressCountry": "GB"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+44-7957-166339",
            "email": "brian@paisley.org.uk",
            "contactType": "customer service"
          },
          "sameAs": [
            "https://www.facebook.com/paisleycommunity",
            "https://x.com/paisleyorguk",
            "https://www.instagram.com/paisleyscotland",
            "https://www.linkedin.com/company/paisley-on-the-web/"
          ]
        })}
      </script>

      {/* Page-specific structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export const SEOPage = {
  Home: () => (
    <SEO
      title="Paisley Business | Local News, Jobs & Business Guide"
      description="The heart of Paisley's business community — local news, jobs, business directory, events and sponsorship opportunities across Renfrewshire, Scotland."
      keywords="Paisley Business, Paisley Scotland, Paisley local news, Paisley jobs, Paisley business guide, Renfrewshire directory, Paisley sponsorship, paisleyscotland.com"
      url={SITE_URL}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Paisley Business | Local News, Jobs & Business Guide",
        "description": "The heart of Paisley's business community — local news, jobs, directory, events and sponsorship.",
        "url": SITE_URL,
        "isPartOf": { "@type": "WebSite", "name": SITE_NAME, "url": SITE_URL },
        "about": {
          "@type": "City",
          "name": "Paisley",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Renfrewshire" }
        }
      }}
    />
  ),

  Sponsors: () => (
    <SEO
      title="Sponsorship Packages | Advertise Your Business in Paisley | Paisley Scotland"
      description="Promote your business to Paisley's community. Choose from Bronze (£199), Silver (£499), Gold (£999) sponsorship packages or a One-Off Event Promotion (£30). Logo placement, social media promotion and event listings included."
      keywords="Paisley sponsorship, business advertising Paisley, sponsor packages Scotland, Renfrewshire business promotion, advertise in Paisley, local advertising Scotland"
      url={`${SITE_URL}/sponsors`}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "OfferCatalog",
        "name": "Paisley Scotland Sponsorship Packages",
        "description": "Business sponsorship and advertising packages for Paisley, Scotland",
        "url": `${SITE_URL}/sponsors`,
        "itemListElement": [
          { "@type": "Offer", "name": "One-Off Event Promotion", "price": "30", "priceCurrency": "GBP", "description": "Event promoted on website, blog and all social media" },
          { "@type": "Offer", "name": "Bronze Sponsorship", "price": "199", "priceCurrency": "GBP", "description": "Business listing and basic promotion" },
          { "@type": "Offer", "name": "Silver Sponsorship", "price": "499", "priceCurrency": "GBP", "description": "Enhanced promotion with logo placement" },
          { "@type": "Offer", "name": "Gold Sponsorship", "price": "999", "priceCurrency": "GBP", "description": "Premium sponsorship with full promotion package" }
        ]
      }}
    />
  ),

  Events: () => (
    <SEO
      title="Events & What's On in Paisley, Scotland | Paisley Scotland"
      description="Discover what's on in Paisley this week. Live music, festivals, community gatherings, markets and more. Your guide to events and things to do in Paisley, Renfrewshire."
      keywords="Paisley events, whats on Paisley, things to do Paisley Scotland, Paisley live music, Paisley festivals, Renfrewshire events"
      url={`${SITE_URL}/events`}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Events & What's On in Paisley",
        "description": "Discover what's on in Paisley this week. Live music, festivals, community gatherings and more.",
        "url": `${SITE_URL}/events`,
        "about": {
          "@type": "City",
          "name": "Paisley",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Renfrewshire" }
        }
      }}
    />
  ),

  Contact: () => (
    <SEO
      title="Contact Us | Get in Touch | Paisley Scotland"
      description="Contact Paisley Scotland for sponsorship enquiries, event promotion, advertising opportunities or general questions about Paisley, Scotland."
      keywords="contact Paisley Scotland, Paisley sponsorship enquiry, advertise Paisley, business support Paisley"
      url={`${SITE_URL}/contact`}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Paisley Scotland",
        "description": "Get in touch for sponsorship, advertising and event promotion enquiries",
        "url": `${SITE_URL}/contact`,
        "mainEntity": {
          "@type": "Organization",
          "name": SITE_NAME,
          "email": "brian@paisley.org.uk"
        }
      }}
    />
  ),

  Register: () => (
    <SEO
      title="Become a Sponsor | Register Your Business | Paisley Scotland"
      description="Register your business on Paisley Scotland. From a £30 one-off event promotion to full sponsorship packages. Reach thousands of local customers in Paisley, Renfrewshire and across Scotland."
      keywords="register business Paisley, become sponsor, Paisley business registration, sponsor signup Scotland, promote business Paisley"
      url={`${SITE_URL}/sponsors`}
    />
  ),

  Jobs: () => (
    <SEO
      title="Paisley & Renfrewshire Jobs | Latest Vacancies | Paisley Scotland"
      description="Latest job vacancies in Paisley and Renfrewshire. Council, NHS, retail, hospitality and professional roles updated daily. Find your next job in Paisley, Scotland."
      keywords="Paisley jobs, Renfrewshire jobs, jobs in Paisley Scotland, Paisley vacancies, job board Paisley, Renfrewshire Council jobs, NHS jobs Paisley"
      url={`${SITE_URL}/jobs`}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Paisley & Renfrewshire Jobs",
        "description": "Latest job vacancies in Paisley and Renfrewshire updated daily.",
        "url": `${SITE_URL}/jobs`,
        "about": {
          "@type": "City",
          "name": "Paisley",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Renfrewshire" }
        }
      }}
    />
  ),

  Properties: () => (
    <SEO
      title="Properties to Let in Paisley & Renfrewshire | Paisley Scotland"
      description="Live rental listings in Paisley and Renfrewshire — flats, houses, and rooms to let. Updated live from Paisley.org.uk."
      keywords="Paisley properties, Paisley flats to rent, Renfrewshire rentals, flats to let Paisley, houses to rent Paisley Scotland"
      url={`${SITE_URL}/properties`}
    />
  ),

  News: () => (
    <SEO
      title="Paisley News & Stories | News, History & Features | Paisley Scotland"
      description="Live news from Paisley and Renfrewshire — local stories, history and features mirrored from Paisley.org.uk. Updated daily."
      keywords="Paisley news, Renfrewshire news, Paisley stories, Paisley history, local news Paisley Scotland, paisley.org.uk"
      url={`${SITE_URL}/news`}
    />
  ),

  Directory: () => (
    <SEO
      title="Paisley Business Directory | Local Businesses in Renfrewshire | Paisley Scotland"
      description="Browse 100+ local businesses across Paisley and Renfrewshire. Restaurants, shops, professional services, healthcare, trades and more. Discover and support your local Paisley businesses."
      keywords="Paisley business directory, businesses in Paisley, local business Paisley, Renfrewshire business directory, shops Paisley, restaurants Paisley"
      url={`${SITE_URL}/directory`}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Paisley Business Directory",
        "description": "Directory of local businesses in Paisley and Renfrewshire.",
        "url": `${SITE_URL}/directory`,
        "about": {
          "@type": "City",
          "name": "Paisley",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Renfrewshire" }
        }
      }}
    />
  ),
};

export default SEO;
